import { convex } from "@/lib/convexClient";
import { api } from "../../../convex/_generated/api";
import { EncryptedS3CredentialService } from "./EncryptedS3CredentialService";

export interface S3Credentials {
  accessKeyId: string;
  secretAccessKey: string;
  endpoint: string;
  region: string;
  bucketName: string;
  useCorsProxy?: boolean;
}

export interface S3Connection {
  id: string;
  craftspace_id: string;
  name: string;
  provider: string;
  bucket_name: string;
  region: string;
  endpoint: string;
  access_key_id?: string; // Legacy plaintext field (deprecated)
  secret_access_key?: string; // Legacy plaintext field (deprecated)
  encrypted_access_key_id?: string;
  encrypted_secret_access_key?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Unified S3 Credential Service with encrypted-only storage.
 * Plaintext credential storage is deprecated and blocked for writes.
 */
export class UnifiedS3CredentialService {
  /**
   * Get S3 credentials for a craftspace.
   */
  static async getCredentials(craftspaceId: string): Promise<S3Credentials | null> {
    try {
      const connection = await convex.query(api.s3Connections.getByCraftspace, { craftspaceId });
      if (!connection) {
        console.log("No S3 connection found for craftspace:", craftspaceId);
        return null;
      }

      if (connection.access_key_id || connection.secret_access_key) {
        console.warn(
          "Legacy plaintext S3 credentials detected for craftspace. Re-save credentials to migrate.",
        );
      }

      if (connection.encrypted_access_key_id && connection.encrypted_secret_access_key) {
        return EncryptedS3CredentialService.getCredentials(craftspaceId);
      }

      console.log("No valid credentials found in connection");
      return null;
    } catch (error) {
      console.error("Error getting S3 credentials:", error);
      return null;
    }
  }

  /**
   * Check if valid credentials exist for a craftspace
   */
  static async hasValidCredentials(craftspaceId: string): Promise<boolean> {
    try {
      const connection = await convex.query(api.s3Connections.getByCraftspace, { craftspaceId });
      if (!connection) {
        return false;
      }

      return !!(connection.encrypted_access_key_id && connection.encrypted_secret_access_key);
    } catch (error) {
      console.error("Error checking credentials validity:", error);
      return false;
    }
  }

  /**
   * Check if credentials require password (encrypted mode)
   */
  static async requiresPassword(craftspaceId: string): Promise<boolean> {
    try {
      void craftspaceId;
      return false;
    } catch (error) {
      console.error("Error checking if password is required:", error);
      return false;
    }
  }

  /**
   * Get encrypted credentials (for production use with password)
   */
  static async getEncryptedCredentials(craftspaceId: string): Promise<S3Credentials | null> {
    // Delegate to the existing encrypted service
    return EncryptedS3CredentialService.getCredentials(craftspaceId);
  }

  /**
   * Store credentials using encrypted storage.
   */
  static async storeCredentials(
    craftspaceId: string,
    credentials: S3Credentials,
    encrypt: boolean = true,
  ): Promise<void> {
    if (!encrypt) {
      console.warn(
        "Ignoring plaintext S3 credential storage request; encrypted storage is required.",
      );
    }
    await EncryptedS3CredentialService.storeCredentials(craftspaceId, credentials);
  }

  /**
   * Remove credentials from the database
   */
  static async removeCredentials(craftspaceId: string): Promise<void> {
    try {
      await convex.mutation(api.s3Connections.remove, { craftspaceId });
    } catch (error) {
      console.error("Error removing S3 credentials:", error);
      throw error;
    }
  }
}
